"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createClient } from "@/lib/supabase/client";
import {
  requestSignupOtp,
  verifySignupOtp,
  setSignupPassword,
} from "@/lib/actions/auth-flows";
import { GOOGLE_OAUTH_ENABLED } from "@/lib/auth/google-oauth";

type Step = "identity" | "code" | "password";

export function SignupForm() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("identity");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [pending, startTransition] = useTransition();
  const [resending, setResending] = useState(false);
  const [resentOnce, setResentOnce] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const onIdentitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startTransition(async () => {
      const result = await requestSignupOtp({ email, name });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setStep("code");
    });
  };

  const onCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startTransition(async () => {
      const result = await verifySignupOtp({ email, otp });
      if (!result.ok) {
        const stale = /invalid|expired|token/i.test(result.error);
        toast.error(stale ? "Invalid or expired code" : result.error, {
          description: stale
            ? "Use the latest email — older codes no longer work."
            : undefined,
        });
        setOtp("");
        return;
      }
      setStep("password");
    });
  };

  const onPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startTransition(async () => {
      const result = await setSignupPassword(password);
      if (!result.ok) {
        toast.error(result.error);
        if (result.needsAuth) {
          // Session lapsed somehow — restart from the top.
          setStep("identity");
          setOtp("");
          setPassword("");
        }
        return;
      }
      toast.success("You're in.");
      router.push(result.redirectTo);
    });
  };

  const onResend = () => {
    if (!email || resending) return;
    setResending(true);
    setOtp("");
    (async () => {
      const result = await requestSignupOtp({ email, name });
      setResending(false);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setResentOnce(true);
      toast.success("New code sent.", {
        description: "Use the latest email — older codes no longer work.",
      });
    })();
  };

  const onGoogle = async () => {
    setGoogleLoading(true);
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=/dashboard`,
      },
    });
    if (error) {
      toast.error(error.message);
      setGoogleLoading(false);
    }
  };

  if (step === "identity") {
    return (
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Tell us where to send a code.
        </p>
        <form onSubmit={onIdentitySubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="signup-name">Name</Label>
            <Input
              id="signup-name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={120}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-email">Email</Label>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full rounded-full"
            size="lg"
            disabled={pending || !name || !email}
          >
            {pending ? "Sending code…" : "Send me a code"}
          </Button>
        </form>

        {GOOGLE_OAUTH_ENABLED && (
          <>
            <div className="relative my-2 text-center text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <span className="bg-background px-3">or</span>
              <span className="absolute inset-x-0 top-1/2 -z-10 h-px bg-border" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-full"
              size="lg"
              onClick={onGoogle}
              disabled={googleLoading}
            >
              {googleLoading ? "Redirecting…" : "Continue with Google"}
            </Button>
          </>
        )}
      </div>
    );
  }

  if (step === "code") {
    return (
      <form onSubmit={onCodeSubmit} className="space-y-5">
        <p className="text-sm text-muted-foreground">
          We sent a code to <span className="text-foreground">{email}</span>.
          Enter it to confirm your email.
        </p>
        <div className="space-y-2">
          <Label htmlFor="signup-otp">Code from email</Label>
          <Input
            id="signup-otp"
            inputMode="numeric"
            pattern="\d{6,10}"
            maxLength={10}
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="Enter the code"
            required
            autoFocus
          />
        </div>
        <Button
          type="submit"
          className="w-full rounded-full"
          size="lg"
          disabled={pending || otp.length < 6}
        >
          {pending ? "Verifying…" : "Continue"}
        </Button>
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => {
              setStep("identity");
              setOtp("");
            }}
            className="text-muted-foreground underline-offset-4 hover:underline"
          >
            Use a different email
          </button>
          <button
            type="button"
            onClick={onResend}
            disabled={resending}
            className="text-muted-foreground underline-offset-4 hover:underline disabled:opacity-50"
          >
            {resending ? "Sending…" : resentOnce ? "Resend again" : "Resend code"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onPasswordSubmit} className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Set a password so you can sign in next time.
      </p>
      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <Input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          maxLength={72}
          autoFocus
        />
        <p className="text-xs text-muted-foreground">At least 8 characters.</p>
      </div>
      <Button
        type="submit"
        className="w-full rounded-full"
        size="lg"
        disabled={pending || password.length < 8}
      >
        {pending ? "Finishing up…" : "Finish"}
      </Button>
    </form>
  );
}
